import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Mail, Lock, User as UserIcon, ArrowRight, ArrowLeft, Loader2 } from "lucide-react";
import { useAuth, apiErr } from "@/context/AuthContext";
import Logo from "@/components/Logo";
import { toast } from "sonner";

function GoogleButton() {
  const go = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/home";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <button onClick={go} type="button" data-testid="google-auth-btn"
      className="w-full flex items-center justify-center gap-3 py-3 rounded-xl border border-[#071126]/12 bg-white font-semibold text-[#071126] hover:border-[#071126]/30 transition-colors pa-focus">
      <svg className="h-5 w-5" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/></svg>
      Continue with Google
    </button>
  );
}

export default function Auth({ mode = "login" }) {
  const isLogin = mode === "login";
  const { login, register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const user = isLogin
        ? await login(form.email, form.password)
        : await register(form.name, form.email, form.password);
      toast.success(isLogin ? "Welcome back!" : "Welcome to PassionAte!");
      nav(user.onboarded ? "/home" : "/onboarding", { replace: true });
    } catch (e2) {
      setErr(apiErr(e2));
    } finally {
      setBusy(false);
    }
  };

  const fillDemo = () => setForm({ name: "", email: "puspa@passionate.app", password: "passion123" });

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#FFFDF8]">
      <div className="hidden lg:block relative">
        <img src="https://images.unsplash.com/photo-1740518904929-33dd20c18a02?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[#071126]/55" />
        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <Link to="/" data-testid="auth-logo-home"><Logo light asDiv /></Link>
          <div>
            <h2 className="text-4xl font-extrabold leading-tight">Find your people.<br />Build your passion.<br /><span className="text-[#FFC94A]">Create your opportunity.</span></h2>
            <p className="text-white/80 mt-4 max-w-md">Join a warm community of makers, movers and creators turning what they love into something more.</p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-sm">
          <Link to="/" data-testid="auth-back-home" className="inline-flex items-center gap-1.5 text-sm font-medium text-[#55708F] hover:text-[#FF6B00] transition-colors mb-6">
            <ArrowLeft className="h-4 w-4" /> Back to home
          </Link>
          <div className="lg:hidden mb-8"><Logo /></div>
          <h1 className="text-3xl font-extrabold tracking-tight text-[#071126]">{isLogin ? "Welcome back" : "Create your account"}</h1>
          <p className="text-[#071126]/60 mt-2">{isLogin ? "Log in to continue your passion journey." : "Start discovering, joining and creating today."}</p>

          <div className="mt-7"><GoogleButton /></div>
          <div className="flex items-center gap-4 my-6"><span className="h-px flex-1 bg-[#071126]/10" /><span className="text-xs text-[#071126]/40 font-medium">OR</span><span className="h-px flex-1 bg-[#071126]/10" /></div>

          <form onSubmit={submit} className="space-y-4">
            {!isLogin && (
              <Field icon={UserIcon} label="Full name">
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Jane Doe" className="pa-input" data-testid="auth-name-input" />
              </Field>
            )}
            <Field icon={Mail} label="Email">
              <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@example.com" className="pa-input" data-testid="auth-email-input" />
            </Field>
            <Field icon={Lock} label="Password">
              <input required type="password" minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="••••••••" className="pa-input" data-testid="auth-password-input" />
            </Field>

            {err && <p className="text-sm text-[#c0392b] bg-[#c0392b]/8 rounded-lg px-3 py-2" data-testid="auth-error">{err}</p>}

            <button type="submit" disabled={busy} data-testid="auth-submit-btn"
              className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-[#FF6B00] text-white font-semibold shadow-[0_6px_18px_rgba(255,107,0,0.28)] hover:bg-[#e05e00] active:scale-[0.99] transition-all disabled:opacity-60 pa-focus">
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <>{isLogin ? "Log in" : "Create account"}<ArrowRight className="h-4 w-4" /></>}
            </button>
          </form>

          {isLogin && (
            <button onClick={fillDemo} className="mt-3 w-full text-center text-sm text-[#55708F] hover:text-[#FF6B00] transition-colors" data-testid="demo-fill-btn">
              Try the demo account →
            </button>
          )}

          <p className="text-center text-sm text-[#071126]/60 mt-6">
            {isLogin ? "New to PassionAte? " : "Already have an account? "}
            <Link to={isLogin ? "/signup" : "/login"} className="font-semibold text-[#FF6B00] hover:underline" data-testid="auth-switch-link">
              {isLogin ? "Create an account" : "Log in"}
            </Link>
          </p>
        </div>
      </div>

      <style>{`.pa-input{width:100%;padding:0.7rem 0.9rem 0.7rem 2.5rem;border-radius:0.75rem;border:1px solid rgba(7,17,38,0.12);background:#fff;font-size:0.925rem;color:#071126;outline:none;transition:border-color .2s}.pa-input:focus{border-color:rgba(255,107,0,0.6);box-shadow:0 0 0 3px rgba(255,107,0,0.12)}`}</style>
    </div>
  );
}

function Field({ icon: Icon, label, children }) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-[#071126]/80 mb-1.5 block">{label}</span>
      <div className="relative">
        <Icon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#55708F]" />
        {children}
      </div>
    </label>
  );
}
